import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Tooltip, Spin, Button } from 'antd';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { useGetDeployTasks } from '@api/emp/queryHooks';
import { getDeployTasks } from '@api/emp/query';

interface IDeployTask {
  username: string;
  status: string;
  triggered_at: number;
  success_at: number;
  description: string;
}
const formatTimestamp = (timestamp: number) => {
  return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
};

const getDateLabel = (timestamp: number) => {
  const date = new Date(timestamp * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate(),
  );

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString();
  }
};

const groupTasksByDate = (tasks: IDeployTask[]) => {
  const grouped: { [key: string]: IDeployTask[] } = {};

  tasks.forEach((task) => {
    const dateLabel = getDateLabel(task.triggered_at);
    if (!grouped[dateLabel]) {
      grouped[dateLabel] = [];
    }
    grouped[dateLabel].push(task);
  });

  return grouped;
};

const formatDescription = (description: string) => {
  if (!description) return '';

  // Check if it's already in the comma-separated format (e.g., "Task: Deployment, Events: test_1, ...")
  if (description.includes('Task:') || (!description.includes('\n') && description.includes(':'))) {
    // Already formatted or in key-value format, return as is
    return description;
  }

  // Extract deployment type and items (for newline-separated format)
  const lines = description.split('\n').filter((line) => line.trim());

  if (lines.length === 0) return description;

  // First line contains the deployment type (e.g., "Deployment Demand:" or "Undeployment Demand:")
  const demandType = lines[0].replace('Demand:', '').trim();

  // Remaining lines contain category and items
  const items: { category: string; names: string[] }[] = [];
  let currentCategory = '';

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check if this is a category line (ends with _to_deploy or _to_undeploy)
    if (line.includes('_to_deploy') || line.includes('_to_undeploy')) {
      currentCategory = line
        .replace('_to_deploy', '')
        .replace('_to_undeploy', '')
        .replace(/_/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .replace('Dt', 'Edited page/screen title');
      items.push({ category: currentCategory, names: [] });
    } else if (currentCategory && line) {
      // This is an item name
      const lastItem = items[items.length - 1];
      if (lastItem) {
        // Split by semicolon in case multiple items are on one line
        const names = line
          .split(';')
          .map((n) => n.trim())
          .filter((n) => n);
        lastItem.names.push(...names);
      }
    }
  }

  // Format output with "Task" as title
  const taskTitle = `Task: ${demandType}`;
  const categoryParts = items
    .filter((item) => item.names.length > 0)
    .map((item) => `${item.category}: ${item.names.join(', ')}`);

  return [taskTitle, ...categoryParts].join(', ');
};

const getStatusDisplay = (status: string) => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'pending') {
    return {
      text: 'In Progress',
      color: '#1890ff',
    };
  } else if (normalizedStatus === 'success') {
    return {
      text: 'Success',
      color: '#52c41a',
      icon: null,
    };
  } else {
    return {
      text: status,
      color: '#ff4d4f',
      icon: null,
    };
  }
};

const DeployStatus = () => {
  const [allTasks, setAllTasks] = useState<IDeployTask[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const currentPageRef = useRef(0);
  const previousPage0DataRef = useRef<string>('');

  // Use hook for page 0 with auto-refresh
  const { data: page0Data, isLoading }: any = useGetDeployTasks({ page: 0, limit: 4 });

  // Handle page 0 data changes (auto-refresh)
  useEffect(() => {
    if (page0Data) {
      const page0DataString = JSON.stringify(page0Data);

      // Check if page 0 data actually changed
      if (previousPage0DataRef.current !== page0DataString) {
        // Reset everything when page 0 data changes
        setAllTasks(page0Data || []);
        previousPage0DataRef.current = page0DataString;
        currentPageRef.current = 0;

        // Reset hasMore if we got a full page
        setHasMore(page0Data.length >= 4);
      }
    }
  }, [page0Data]);

  // Get the most recent deployment status (first item in the array)
  const lastDeploymentStatus = allTasks?.[0]?.status?.toLowerCase();

  const handleLoadMore = useCallback(async () => {
    const nextPage = currentPageRef.current + 1;
    setLoadingMore(true);

    try {
      // Use API call directly for load more (no auto-refresh)
      const response = await getDeployTasks({ page: nextPage, limit: 4 });
      const newData = response?.data?.data || [];

      // Append new data to existing tasks
      setAllTasks((prev) => [...prev, ...newData]);
      currentPageRef.current = nextPage;

      // Check if there are more pages
      if (newData.length < 4) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more deploy tasks:', error);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  const renderContent = () => {
    if (isLoading && allTasks.length === 0) {
      return <Spin size="small" />;
    }

    if (allTasks.length === 0) {
      return <div>No deployment history</div>;
    }

    const groupedTasks = groupTasksByDate(allTasks);
    const dateLabels = Object.keys(groupedTasks);

    return (
      <div
        style={{ maxWidth: 300, maxHeight: 700, overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <strong>Recent Deploy History</strong>
        {dateLabels.map((dateLabel, groupIndex) => (
          <div
            key={dateLabel}
            style={{
              paddingBottom: 12,
              marginBottom: 12,
              borderBottom: groupIndex < dateLabels.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <div
              style={{
                marginBottom: 8,
                fontSize: 13,
              }}
            >
              {dateLabel}
            </div>
            {groupedTasks[dateLabel].map((task: IDeployTask, index: number) => {
              const statusDisplay = getStatusDisplay(task.status);

              return (
                <div
                  key={`${dateLabel}-${index}`}
                  style={{
                    marginBottom: index < groupedTasks[dateLabel].length - 1 ? 12 : 0,
                  }}
                >
                  <div style={{ marginBottom: 4 }}>
                    <span
                      style={{
                        color: statusDisplay.color,
                        border: `1px solid ${statusDisplay.color}`,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        display: 'inline-block',
                      }}
                    >
                      {statusDisplay.text}
                    </span>
                  </div>
                  {task.status.toLowerCase() === 'success' ? (
                    <>
                      <div style={{ marginBottom: 4 }}>
                        Triggered At: {formatTimestamp(task.triggered_at)}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        Deployed At: {formatTimestamp(task.success_at)}
                      </div>
                      <div style={{ marginBottom: 4 }}>Deployed By: {task.username}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: 4 }}>
                        Triggered At: {formatTimestamp(task.triggered_at)}
                      </div>
                      <div style={{ marginBottom: 4 }}>Triggered By: {task.username}</div>
                    </>
                  )}
                  <div style={{ fontSize: 12, color: '#aca6a6', marginTop: 4 }}>
                    {formatDescription(task.description)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {hasMore && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #f0f0f0',
            }}
          >
            <Button size="small" type="primary" onClick={handleLoadMore} loading={loadingMore}>
              Load More
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Tooltip title={renderContent()} placement="bottomLeft" overlayStyle={{ maxWidth: 450 }}>
      {lastDeploymentStatus === 'pending' ? (
        <Spin size="small" />
      ) : (
        <InfoCircleOutlined
          style={{
            marginLeft: 5,
            color: lastDeploymentStatus === 'failed' ? '#ff4d4f' : '#52c41a',
          }}
        />
      )}
    </Tooltip>
  );
};

export default DeployStatus;
